// The tutor clips of the open dialog, keyed by audioId, for phrase replay.
//
// Before phrase replay, each AudioPlayer fetched and held its own copy of its
// clip. A phrase tap starts in TutorMessage, not in the pill, and plays on the
// playback service's second player, so the bytes have to live somewhere both
// can reach. This cache is that place. getClip() returns the cached blob, or
// starts one fetch that every concurrent caller shares.
//
// The rules, each of which a test in tests/unit/clipCache.test.js pins:
//
//   - Admission is decided by the callers, not here. Only a pill that carries
//     playbackControls (a tutor message in a lesson or a freestyle chat), and
//     only while phraseReplay is on, fetches through the cache, and the
//     playback service prefetches only such a message's stitched clip. Student
//     clips, the admin viewer and Daily Play keep their own fetch and never
//     take a slot.
//   - A failed fetch is never cached, so the next tap tries again.
//   - A shared fetch is aborted only when no consumer is waiting on it any
//     more. Each consumer passes its own AbortSignal, and aborting it rejects
//     that consumer alone.
//   - A clip that a mounted pill holds (holdClip) or that either player holds
//     (registerPlayerHolds) is never evicted. Of the rest, at most
//     MAX_UNHELD_CLIPS are kept, least recently used first out. So the cache
//     holds every clip of the open dialog, the same memory the mounted pills
//     held before, plus at most eight clips whose pills have unmounted.
//   - An object URL is created once per clip on demand (clipObjectUrl) and
//     revoked only when the clip is evicted or the cache is cleared. The slice
//     player plays from it and must never revoke it, because the next slice or
//     loop iteration needs it again.
//   - The cache belongs to the open dialog: ChatScreen clears it on a dialog
//     switch, and src/scripts/phraseReplaySync.js clears it when the signed-in
//     account goes away. A fetch that finishes after a clear still answers the
//     consumers that asked for it, but it is not put back into the cache.
//
// This module imports nothing but Vue. The fetch itself is registered from
// outside (registerClipFetcher, called by phraseReplaySync.js with the same
// getAudio request AudioPlayer makes), so the playback service and the pill can
// import the cache without pulling Firebase or the auth store into the unit
// tests that load them.

import { reactive } from "vue";

export const MAX_UNHELD_CLIPS = 8;

// Whether phraseReplay is on for the signed-in learner. Fed by
// src/scripts/phraseReplaySync.js. The pill uses the cache, and the service
// prefetches through it, only while this is true, so the flag is also the kill
// switch for the shared fetch.
export const clipCacheState = reactive({
  enabled: false,
});

let clipFetcher = null;
let isHeldByPlayer = () => false;

// audioId -> entry. An entry is either settled with a blob, or pending with
// the consumers waiting on its fetch.
const entries = new Map();
// audioId -> the number of mounted pills that hold that clip. Kept apart from
// the entries so a pill can hold a clip before its bytes have arrived.
const holdCounts = new Map();
let useSequence = 0;
// Bumped by every clear, so a caller holding bytes it fetched before a clear
// can tell they belong to a cache that no longer exists.
let clearGeneration = 0;

/** Changes whenever the cache is cleared. */
export function clipCacheGeneration() {
  return clearGeneration;
}

/**
 * Registers the function that fetches a clip's bytes:
 * (audioId, {priority, signal}) => Promise<Blob>.
 */
export function registerClipFetcher(fetcher) {
  clipFetcher = typeof fetcher === "function" ? fetcher : null;
}

/**
 * Registers the playback service's answer to "does either player hold this
 * clip right now". A held clip is never evicted.
 */
export function registerPlayerHolds(check) {
  isHeldByPlayer = typeof check === "function" ? check : () => false;
}

function abortError() {
  const error = new Error("The clip request was aborted");
  error.name = "AbortError";
  return error;
}

function touch(entry) {
  entry.lastUsed = ++useSequence;
}

/**
 * The clip's bytes: the cached blob, or one fetch shared by every caller.
 * @param {string} audioId
 * @param {{priority?: string, signal?: AbortSignal}} options priority is the
 *   fetch priority hint, and only the caller that starts the fetch sets it: a
 *   browser cannot change a request's priority once it is sent, so a phrase
 *   tap that joins a low-priority prefetch waits on that request as it is.
 *   signal aborts this caller only.
 * @returns {Promise<Blob>}
 */
export function getClip(audioId, { priority = "low", signal = null } = {}) {
  if (!audioId) return Promise.reject(new Error("No audioId to fetch"));
  if (signal && signal.aborted) return Promise.reject(abortError());
  let entry = entries.get(audioId);
  if (entry && entry.blob) {
    touch(entry);
    return Promise.resolve(entry.blob);
  }
  if (!entry) {
    if (!clipFetcher) {
      return Promise.reject(new Error("No clip fetcher has been registered"));
    }
    entry = startFetch(audioId, priority);
  }
  touch(entry);
  return addWaiter(entry, signal);
}

function startFetch(audioId, priority) {
  const controller = new AbortController();
  const entry = {
    audioId,
    blob: null,
    objectUrl: null,
    controller,
    waiters: new Set(),
    settled: false,
    lastUsed: 0,
  };
  entries.set(audioId, entry);
  let request;
  try {
    request = Promise.resolve(clipFetcher(audioId, { priority, signal: controller.signal }));
  } catch (error) {
    request = Promise.reject(error);
  }
  request.then(
    (blob) => settleEntry(entry, blob, null),
    (error) => settleEntry(entry, null, error)
  );
  return entry;
}

// The fetch finished, or a pill handed over its own blob. Every waiter gets the
// answer. The blob is kept only if the entry is still the cache's own, which it
// is not after a clear.
function settleEntry(entry, blob, error) {
  if (entry.settled) return;
  entry.settled = true;
  entry.controller = null;
  const stillCached = entries.get(entry.audioId) === entry;
  // An empty body counts as a failure too, so it is not kept for the rest of
  // the dialog and the next tap fetches again.
  const empty = !blob || (typeof blob.size === "number" && blob.size === 0);
  const failure = error || (empty ? new Error("The clip fetch returned no bytes") : null);
  if (failure) {
    if (stillCached) entries.delete(entry.audioId);
  } else if (stillCached) {
    entry.blob = blob;
  }
  const waiters = [...entry.waiters];
  entry.waiters.clear();
  waiters.forEach((waiter) => {
    waiter.detach();
    if (failure) waiter.reject(failure);
    else waiter.resolve(blob);
  });
  if (!failure && stillCached) evictUnheldClips();
}

function addWaiter(entry, signal) {
  return new Promise((resolve, reject) => {
    const waiter = { resolve, reject, detach: () => {} };
    if (signal) {
      const onAbort = () => {
        if (!entry.waiters.has(waiter)) return;
        entry.waiters.delete(waiter);
        reject(abortError());
        // The last consumer gave up, so nobody would receive these bytes.
        if (!entry.settled && entry.waiters.size === 0) {
          const controller = entry.controller;
          entry.settled = true;
          entry.controller = null;
          if (entries.get(entry.audioId) === entry) entries.delete(entry.audioId);
          if (controller) controller.abort();
        }
      };
      signal.addEventListener("abort", onAbort, { once: true });
      waiter.detach = () => signal.removeEventListener("abort", onAbort);
    }
    entry.waiters.add(waiter);
  });
}

/** The cached blob, or null. Never fetches. */
export function peekClip(audioId) {
  const entry = audioId ? entries.get(audioId) : null;
  return entry && entry.blob ? entry.blob : null;
}

/**
 * A pill that fetched its clip through its own path, before phraseReplay
 * turned on, hands its blob over rather than having the cache fetch it again.
 * A clip already cached is kept as it is. A fetch still in flight is answered
 * with the handed-over blob and then aborted.
 */
export function adoptClip(audioId, blob) {
  if (!audioId || !blob) return;
  const entry = entries.get(audioId);
  if (entry && entry.blob) return;
  if (entry && !entry.settled) {
    const controller = entry.controller;
    touch(entry);
    settleEntry(entry, blob, null);
    if (controller) controller.abort();
    return;
  }
  entries.set(audioId, {
    audioId,
    blob,
    objectUrl: null,
    controller: null,
    waiters: new Set(),
    settled: true,
    lastUsed: ++useSequence,
  });
  evictUnheldClips();
}

/** A mounted pill holds its clip, so the clip is never evicted. */
export function holdClip(audioId) {
  if (!audioId) return;
  holdCounts.set(audioId, (holdCounts.get(audioId) || 0) + 1);
}

/** The pill unmounted, or stopped using the cache. */
export function releaseClip(audioId) {
  if (!audioId) return;
  const count = (holdCounts.get(audioId) || 0) - 1;
  if (count > 0) holdCounts.set(audioId, count);
  else holdCounts.delete(audioId);
  evictUnheldClips();
}

/**
 * The object URL the slice player plays the clip from, created on first use.
 * Null when the clip is not cached. The caller must never revoke it.
 */
export function clipObjectUrl(audioId) {
  const entry = audioId ? entries.get(audioId) : null;
  if (!entry || !entry.blob) return null;
  if (!entry.objectUrl) entry.objectUrl = URL.createObjectURL(entry.blob);
  touch(entry);
  return entry.objectUrl;
}

function isHeld(audioId) {
  if ((holdCounts.get(audioId) || 0) > 0) return true;
  try {
    return isHeldByPlayer(audioId) === true;
  } catch {
    // An answer we cannot get is treated as held: evicting a clip a player is
    // using would pull its bytes out from under it.
    return true;
  }
}

function dropEntry(entry) {
  if (entry.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
    entry.objectUrl = null;
  }
  entries.delete(entry.audioId);
}

/**
 * Evicts least recently used clips until at most MAX_UNHELD_CLIPS clips that
 * nothing holds remain. A pending fetch is never evicted, because a consumer is
 * waiting on it. Exported so the playback service can call it when a player
 * lets go of a clip.
 */
export function evictUnheldClips() {
  const unheld = [];
  entries.forEach((entry) => {
    if (entry.blob && !isHeld(entry.audioId)) unheld.push(entry);
  });
  if (unheld.length <= MAX_UNHELD_CLIPS) return;
  unheld.sort((a, b) => a.lastUsed - b.lastUsed);
  unheld.slice(0, unheld.length - MAX_UNHELD_CLIPS).forEach(dropEntry);
}

/**
 * Empties the cache: on a dialog switch, and when the signed-in account goes
 * away. Every object URL is revoked. A fetch still in flight keeps running for
 * the consumers waiting on it, is aborted as soon as none is left, and is not
 * put back into the cache when it finishes.
 */
export function clearClipCache() {
  clearGeneration++;
  const all = [...entries.values()];
  entries.clear();
  all.forEach((entry) => {
    if (entry.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
      entry.objectUrl = null;
    }
    if (!entry.settled && entry.waiters.size === 0 && entry.controller) {
      entry.settled = true;
      entry.controller.abort();
      entry.controller = null;
    }
  });
}
